import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import { useStaticQuery, graphql } from 'gatsby'

import { COUNTRIES, LOCALIZATION } from 'src/utils/constants'

const MAX_META_DESCRIPTION_LENGTH = 160
const ELLIPSIS = '...'
const DEFAULT_BASE_URL = 'https://myskylight.com/'
const BASE_URL = COUNTRIES[LOCALIZATION]?.link || DEFAULT_BASE_URL // MUST HAVE TRAILING SLASH LIKE DEFAULT BASE URL

function SEO({
  description,
  lang,
  meta,
  title,
  path,
  imagePath,
  canonicalPath,
  excludeTitleTagPrefix,
  hrefLangPathOverrides,
}) {
  const { site, ogImage } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
          }
        }
        ogImage: file(relativePath: { eq: "hero-image.webp" }) {
          childImageSharp {
            gatsbyImageData(layout: FULL_WIDTH, placeholder: NONE)
          }
        }
      }
    `
  )

  let metaDescription = description || site.siteMetadata.description
  if (metaDescription.length > MAX_META_DESCRIPTION_LENGTH - ELLIPSIS.length) {
    metaDescription = metaDescription.substring(0, MAX_META_DESCRIPTION_LENGTH) + ELLIPSIS
  }

  const fullImageUrlFromPath = () => {
    if (!imagePath)
      return [BASE_URL, ogImage.childImageSharp.gatsbyImageData.images.fallback.src].join('')
    if (imagePath.startsWith('/')) return [BASE_URL, imagePath.substring(1)].join('')
    return imagePath
  }

  const fullCanonicalUrlFor = (baseUrl, pathToAppend) => {
    // Remove the trailing slash from BASE_URL to avoid double slashes
    const urlParts = [baseUrl.slice(0, -1)]
    if (!pathToAppend.startsWith('/')) urlParts.push('/')
    urlParts.push(pathToAppend)
    if (!pathToAppend.endsWith('/')) urlParts.push('/')
    return urlParts.join('')
  }

  // In rare cases (as far as I know, only the 15" Calendar Product Page)
  // the canonical path is different on International sites.
  // This allows us to override the hrefLang paths on a per-page basis.
  const hrefLangPathFor = (key) => {
    if (!hrefLangPathOverrides || !hrefLangPathOverrides[key]) {
      return canonicalPath
    }
    return hrefLangPathOverrides[key]
  }

  const hrefLangAlternates = () => {
    const alternateMap = {}
    if (!Boolean(canonicalPath)) return alternateMap
    Object.keys(COUNTRIES).forEach((key) => {
      const hrefLangKey = key === 'en' ? 'en-us' : key
      alternateMap[hrefLangKey] = fullCanonicalUrlFor(
        COUNTRIES[key].link,
        hrefLangPathFor(hrefLangKey)
      )
    })
    alternateMap['x-default'] = fullCanonicalUrlFor(DEFAULT_BASE_URL, hrefLangPathFor('x-default'))
    return alternateMap
  }

  const metaTags = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:site_name`,
      content: title || site.siteMetadata.title,
    },
    {
      property: `og:title`,
      content: title,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: site.siteMetadata.author,
    },
    {
      name: `twitter:title`,
      content: title,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
  ].concat(meta)
  if (path) {
    metaTags.push({
      property: `og:url`,
      content: [BASE_URL, path].join(''),
    })
  }
  metaTags.push({
    property: `og:image`,
    content: fullImageUrlFromPath(),
  })
  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={excludeTitleTagPrefix ? undefined : 'Skylight | %s'}
      meta={metaTags}
    >
      {/* Today I Learned: Helmet does not support <></> or React.Fragment :facepalm: */}
      {Boolean(canonicalPath) && (
        <link rel="canonical" href={fullCanonicalUrlFor(BASE_URL, canonicalPath)} />
      )}
      {Object.entries(hrefLangAlternates()).map(([key, value]) => (
        <link rel="alternate" hrefLang={key} href={value} />
      ))}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  path: PropTypes.string,
  imagePath: PropTypes.string,
  canonicalPath: PropTypes.string,
  excludeTitleTagPrefix: PropTypes.bool,
  hrefLangPathOverrides: PropTypes.objectOf(PropTypes.string),
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO
